import { formatNumber, i18n } from './i18n'
import type { ApiProblem, JsonObject } from './types'
import { asObject, humanize, resourceLabel, uiText } from './ui'

export interface GameErrorNotice {
  title: string
  message: string
  details: string[]
  code: string
  correlationId?: string
  status?: number
}

const GENERIC_SERVER_DETAILS = new Set([
  'The request could not be processed.',
  'Request rejected',
  'Invalid JSON',
  'The request body is invalid.',
])

const exactMessages: Record<string, [string, string]> = {
  TRAIN_NOT_ENOUGH_RESOURCES: [
    'Nicht genug Rohstoffe für die Rekrutierung.',
    'Not enough resources for recruitment.',
  ],
  TRAIN_NOT_ENOUGH_POPULATION: [
    'Nicht genug freie Bevölkerung für diese Rekrutierung.',
    'Not enough free population for this recruitment.',
  ],
  TRAINING_QUEUE_FULL: [
    'Die Rekrutierungswarteschlange ist voll.',
    'The recruitment queue is full.',
  ],
  TRAINING_BUILDING_UNAVAILABLE: [
    'Das benötigte Ausbildungsgebäude ist nicht verfügbar.',
    'The required training building is unavailable.',
  ],
  UNIT_DISABLED: [
    'Diese Einheit ist in dieser Welt deaktiviert.',
    'This unit is disabled in this world.',
  ],
  UNIT_NOT_UNLOCKED: [
    'Diese Einheit ist noch nicht freigeschaltet.',
    'This unit is not unlocked yet.',
  ],
  UNIT_UNKNOWN: ['Diese Einheit ist unbekannt.', 'This unit is unknown.'],
  BUILD_NOT_ENOUGH_RESOURCES: [
    'Nicht genug Rohstoffe für diesen Bauauftrag.',
    'Not enough resources for this building order.',
  ],
  BUILD_NOT_ENOUGH_POPULATION: [
    'Nicht genug freie Bevölkerung für diesen Bauauftrag.',
    'Not enough free population for this building order.',
  ],
  BUILDING_QUEUE_FULL: [
    'Die Bauwarteschlange ist voll.',
    'The building queue is full.',
  ],
  BUILDING_PREREQUISITE_FAILED: [
    'Eine oder mehrere Bauvoraussetzungen fehlen.',
    'One or more building requirements are missing.',
  ],
  BUILDING_MAX_LEVEL: [
    'Dieses Gebäude hat bereits die maximale Stufe erreicht.',
    'This building is already at its maximum level.',
  ],
  BUILDING_DISABLED: [
    'Dieses Gebäude ist in dieser Welt deaktiviert.',
    'This building is disabled in this world.',
  ],
  MARKET_NOT_ENOUGH_RESOURCES: [
    'Nicht genug Rohstoffe für diese Marktaktion.',
    'Not enough resources for this market action.',
  ],
  MARKET_NOT_ENOUGH_MERCHANTS: [
    'Nicht genug freie Händler für diese Marktaktion.',
    'Not enough free merchants for this market action.',
  ],
  MARKET_BUILDING_UNAVAILABLE: [
    'Der Markt ist in diesem Dorf nicht verfügbar.',
    'The market is unavailable in this village.',
  ],
  MARKET_CAPACITY_INVALID: [
    'Die Händlerkapazität reicht für diese Menge nicht aus.',
    'Merchant capacity is insufficient for this amount.',
  ],
  NOBILITY_NOT_ENOUGH_GOLD: [
    'Nicht genug Gold für diese Aktion.',
    'Not enough gold for this action.',
  ],
  NOBILITY_MINT_NOT_ENOUGH_RESOURCES: [
    'Nicht genug Rohstoffe zum Prägen.',
    'Not enough resources for minting.',
  ],
  NOBILITY_BUILDING_UNAVAILABLE: [
    'Das benötigte Adelsgebäude ist nicht verfügbar.',
    'The required nobility building is unavailable.',
  ],
  MOVEMENT_NOT_AVAILABLE: [
    'Diese Truppenbewegung ist derzeit nicht möglich.',
    'This troop movement is currently unavailable.',
  ],
  MOVEMENT_UNIT_COMPOSITION_INVALID: [
    'Die ausgewählte Truppenzusammenstellung ist ungültig.',
    'The selected troop composition is invalid.',
  ],
  MOVEMENT_RECALL_WINDOW_CLOSED: [
    'Diese Truppenbewegung kann nicht mehr zurückgerufen werden.',
    'This troop movement can no longer be recalled.',
  ],
  ATTACK_BLOCKED_BY_MORALE: [
    'Dieser Angriff ist durch die Moralregel blockiert.',
    'This attack is blocked by the morale rule.',
  ],
  ESPIONAGE_SCOUT_REQUIRED: [
    'Für Spionage werden Späher benötigt.',
    'Scouts are required for espionage.',
  ],
  CONTENT_RESOURCE_INSUFFICIENT: [
    'Für diese Aktion fehlen Rohstoffe.',
    'Resources are missing for this action.',
  ],
  EVENT_RESOURCE_INSUFFICIENT: [
    'Für diese Event-Aktion fehlen Rohstoffe.',
    'Resources are missing for this event action.',
  ],
  AUTH_REQUIRED: [
    'Deine Sitzung ist nicht mehr gültig. Bitte melde dich erneut an.',
    'Your session is no longer valid. Please sign in again.',
  ],
  AUTH_FORBIDDEN: [
    'Du hast für diese Aktion keine Berechtigung.',
    'You do not have permission for this action.',
  ],
  PROFILE_CONTRACT_INVALID: [
    'Die Weltkonfiguration ist intern inkonsistent. Bitte melde diesen Fehler.',
    'The world configuration is internally inconsistent. Please report this error.',
  ],
  VALIDATION_FAILED: [
    'Die Eingabe ist unvollständig oder ungültig.',
    'The input is incomplete or invalid.',
  ],
  IDEMPOTENCY_KEY_REQUIRED: [
    'Die Aktion konnte nicht sicher verarbeitet werden. Bitte erneut versuchen.',
    'The action could not be processed safely. Please try again.',
  ],
  REVISION_CONFLICT: [
    'Der Spielstand hat sich inzwischen geändert. Bitte erneut versuchen.',
    'The game state changed in the meantime. Please try again.',
  ],
  CONFLICT_RETRY: [
    'Der Spielstand hat sich gleichzeitig geändert. Bitte erneut versuchen.',
    'The game state changed concurrently. Please try again.',
  ],
  OFFLINE_COMMAND_BLOCKED: [
    'Du bist offline. Spielaktionen können erst nach Wiederherstellung der Verbindung ausgeführt werden.',
    'You are offline. Game actions can only be performed after reconnecting.',
  ],
  NETWORK_ERROR: [
    'Der Server ist gerade nicht erreichbar. Bitte Verbindung prüfen und erneut versuchen.',
    'The server is currently unreachable. Check your connection and try again.',
  ],
  MOVEMENT_CALCULATION_INVALID: [
    'Die Bewegungsberechnung konnte mit dem aktuellen Weltzustand nicht durchgeführt werden.',
    'The movement calculation could not be completed with the current world state.',
  ],
  MOVEMENT_INTERNAL_ERROR: [
    'Beim Verarbeiten der Truppenbewegung ist ein interner Serverfehler aufgetreten. Bitte mit der angezeigten Referenz melden.',
    'An internal server error occurred while processing the troop movement. Please report it with the displayed reference.',
  ],
  UX_INTERNAL_ERROR: [
    'Beim Laden der Spieldaten ist ein interner Serverfehler aufgetreten. Bitte mit der angezeigten Referenz melden.',
    'An internal server error occurred while loading game data. Please report it with the displayed reference.',
  ],
  INVALID_SERVER_RESPONSE: [
    'Der Server hat eine ungültige Antwort geliefert. Bitte mit der angezeigten Referenz melden.',
    'The server returned an invalid response. Please report it with the displayed reference.',
  ],
}

function localized(pair: [string, string]): string {
  return i18n.locale === 'de' ? pair[0] : pair[1]
}

function genericMessage(code: string): string {
  if (code.endsWith('_NOT_ENOUGH_RESOURCES') || code.endsWith('_RESOURCE_INSUFFICIENT')) {
    return uiText(
      'Für diese Aktion sind nicht genug Rohstoffe vorhanden.',
      'There are not enough resources for this action.',
    )
  }
  if (code.endsWith('_NOT_ENOUGH_POPULATION')) {
    return uiText(
      'Für diese Aktion ist nicht genug freie Bevölkerung vorhanden.',
      'There is not enough free population for this action.',
    )
  }
  if (code.endsWith('_QUEUE_FULL')) {
    return uiText('Die Warteschlange ist voll.', 'The queue is full.')
  }
  if (code.endsWith('_NOT_FOUND')) {
    return uiText(
      'Das angeforderte Objekt wurde nicht gefunden.',
      'The requested item was not found.',
    )
  }
  if (code.endsWith('_FORBIDDEN')) {
    return uiText(
      'Diese Aktion ist für dich nicht erlaubt.',
      'You are not allowed to perform this action.',
    )
  }
  if (code.endsWith('_DISABLED')) {
    return uiText(
      'Diese Funktion ist in dieser Welt deaktiviert.',
      'This feature is disabled in this world.',
    )
  }
  if (code.endsWith('_NOT_ACTIVE')) {
    return uiText(
      'Diese Funktion ist derzeit nicht aktiv.',
      'This feature is currently not active.',
    )
  }
  if (code.endsWith('_UNAVAILABLE') || code.endsWith('_NOT_AVAILABLE')) {
    return uiText(
      'Diese Aktion ist unter den aktuellen Bedingungen nicht verfügbar.',
      'This action is unavailable under the current conditions.',
    )
  }
  if (
    code.endsWith('_REVISION_CONFLICT') ||
    code.endsWith('_STATE_CONFLICT') ||
    code.endsWith('_STALE') ||
    code.endsWith('_STALE_JOB')
  ) {
    return uiText(
      'Der Spielstand hat sich inzwischen geändert. Lade die aktuellen Daten und versuche es erneut.',
      'The game state changed in the meantime. Reload the current data and try again.',
    )
  }
  if (code.endsWith('_LIMIT_EXCEEDED') || code.endsWith('_LIMIT')) {
    return uiText(
      'Das zulässige Limit für diese Aktion ist erreicht.',
      'The allowed limit for this action has been reached.',
    )
  }
  if (code.endsWith('_COOLDOWN')) {
    return uiText(
      'Diese Aktion ist noch in Abklingzeit.',
      'This action is still on cooldown.',
    )
  }
  if (code.endsWith('_REQUIRED') || code.endsWith('_MISSING')) {
    return uiText(
      'Eine benötigte Voraussetzung fehlt.',
      'A required prerequisite is missing.',
    )
  }
  if (code.endsWith('_EXISTS')) {
    return uiText(
      'Dieser Eintrag existiert bereits.',
      'This entry already exists.',
    )
  }
  if (code.endsWith('_INVALID')) {
    return uiText(
      'Die eingegebenen oder ausgewählten Daten sind ungültig.',
      'The entered or selected data is invalid.',
    )
  }
  if (code.startsWith('HTTP_')) {
    return uiText(
      'Der Server konnte die Anfrage nicht verarbeiten.',
      'The server could not process the request.',
    )
  }
  return uiText(
    'Die Aktion konnte nicht ausgeführt werden. Bitte prüfe die Voraussetzungen und versuche es erneut.',
    'The action could not be completed. Check the requirements and try again.',
  )
}

export function gameErrorMessage(problem: ApiProblem): string {
  const exact = exactMessages[problem.code]
  if (exact) return localized(exact)
  if (
    typeof problem.detail === 'string' &&
    problem.detail.trim() !== '' &&
    !GENERIC_SERVER_DETAILS.has(problem.detail.trim())
  ) {
    return problem.detail.trim()
  }
  return genericMessage(problem.code)
}

function microAmount(value: unknown): string {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return '—'
  return formatNumber(numeric / 1_000_000, 2)
}

function integerAmount(value: unknown): string {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return '—'
  return formatNumber(numeric, 0)
}

function blockerText(blocker: JsonObject): string | null {
  const code = typeof blocker.code === 'string' ? blocker.code : ''
  if (code === 'RESOURCE') {
    const type = typeof blocker.resourceType === 'string' ? blocker.resourceType : ''
    return uiText(
      `${resourceLabel(type)}: es fehlen ${microAmount(blocker.missingMicro)} (vorhanden ${microAmount(blocker.availableMicro)}, benötigt ${microAmount(blocker.requiredMicro)}).`,
      `${resourceLabel(type)}: ${microAmount(blocker.missingMicro)} missing (${microAmount(blocker.availableMicro)} available, ${microAmount(blocker.requiredMicro)} required).`,
    )
  }
  if (code === 'POPULATION') {
    const required = Number(blocker.requiredAdditional ?? blocker.required ?? 0)
    const free = Number(blocker.free ?? blocker.available ?? 0)
    const missing = Math.max(0, required - free)
    return uiText(
      `Bevölkerung: ${integerAmount(missing)} fehlen (${integerAmount(free)} frei, ${integerAmount(required)} benötigt).`,
      `Population: ${integerAmount(missing)} missing (${integerAmount(free)} free, ${integerAmount(required)} required).`,
    )
  }
  if (code === 'BUILDING_MIN_LEVEL') {
    const building = typeof blocker.buildingType === 'string' ? humanize(blocker.buildingType) : uiText('Gebäude', 'Building')
    return uiText(
      `${building}: Stufe ${integerAmount(blocker.requiredLevel)} benötigt, aktuell Stufe ${integerAmount(blocker.currentLevel)}.`,
      `${building}: level ${integerAmount(blocker.requiredLevel)} required, currently level ${integerAmount(blocker.currentLevel)}.`,
    )
  }
  if (code === 'RESEARCH_MIN_LEVEL') {
    const research = typeof blocker.researchKey === 'string' ? humanize(blocker.researchKey) : uiText('Forschung', 'Research')
    return uiText(
      `${research}: Stufe ${integerAmount(blocker.requiredLevel)} benötigt, aktuell Stufe ${integerAmount(blocker.currentLevel)}.`,
      `${research}: level ${integerAmount(blocker.requiredLevel)} required, currently level ${integerAmount(blocker.currentLevel)}.`,
    )
  }
  if (code === 'QUEUE_CAPACITY') {
    return uiText(
      `Warteschlange voll: ${integerAmount(blocker.active)}/${integerAmount(blocker.activeLimit)} aktiv und ${integerAmount(blocker.waiting)}/${integerAmount(blocker.waitingLimit)} wartend.`,
      `Queue full: ${integerAmount(blocker.active)}/${integerAmount(blocker.activeLimit)} active and ${integerAmount(blocker.waiting)}/${integerAmount(blocker.waitingLimit)} waiting.`,
    )
  }
  if (code === 'BUILDING_ORDER_CONFLICT') {
    return uiText(
      'Für dieses Gebäude läuft bereits ein Auftrag.',
      'There is already an order for this building.',
    )
  }
  if (code === 'LAYOUT_SLOT_AVAILABLE') {
    return uiText(
      'Für dieses Gebäude ist kein passender Bauplatz frei.',
      'No suitable building slot is available for this building.',
    )
  }
  if (code === 'VILLAGE_TYPE') {
    return uiText(
      'Dieser Gebäudetyp ist für diesen Dorftyp nicht erlaubt.',
      'This building type is not allowed for this village type.',
    )
  }
  if (code === 'LAYOUT_SLOT') {
    return uiText(
      'Die Bauplatz-Voraussetzung ist nicht erfüllt.',
      'The building-slot requirement is not met.',
    )
  }
  return null
}

function fieldErrorText(field: string, messages: string[]): string {
  const clean = messages
    .map((message) => message.trim())
    .filter((message) => message !== '')
    .map((message) =>
      /^[A-Z][A-Z0-9_]+$/.test(message)
        ? genericMessage(message)
        : message,
    )
  const suffix = clean.length > 0 ? clean.join(' ') : uiText('Ungültige Eingabe.', 'Invalid input.')
  return `${humanize(field)}: ${suffix}`
}

export function gameErrorDetails(problem: ApiProblem): string[] {
  const details: string[] = []
  for (const raw of problem.blockers ?? []) {
    const blocker = asObject(raw)
    const line = blockerText(blocker)
    if (line && !details.includes(line)) details.push(line)
  }
  for (const [field, messages] of Object.entries(problem.fieldErrors ?? {})) {
    const values = Array.isArray(messages)
      ? messages.filter((value): value is string => typeof value === 'string')
      : []
    details.push(fieldErrorText(field, values))
  }
  return details.slice(0, 8)
}

export function gameErrorNotice(problem: ApiProblem, status?: number): GameErrorNotice {
  const notice: GameErrorNotice = {
    title: uiText('Aktion nicht möglich', 'Action could not be completed'),
    message: gameErrorMessage(problem),
    details: gameErrorDetails(problem),
    code: problem.code,
  }
  if (problem.correlationId) notice.correlationId = problem.correlationId
  if (status !== undefined) notice.status = status
  return notice
}

export function announceGameError(problem: ApiProblem, status?: number): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(
    new CustomEvent<GameErrorNotice>('ci:command-error', {
      detail: gameErrorNotice(problem, status),
    }),
  )
}
